import { CircleCheck, TriangleAlert } from "lucide-react";
import * as React from "react";

import { type DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { useDeleteDeviceType } from "@/shared/domain/deviceTypes/hooks";
import {
  Banner,
  Dialog,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { isDeleteConfirmed } from "@/shared/text";

import styles from "./DeleteDeviceTypeDialog.module.css";

interface DeleteDeviceTypeDialogProps {
  deviceType: DeviceTypeRecord;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Confirms deleting a device type. While any device has the type, the service
 * refuses the delete, so the dialog explains how to change those devices' type
 * and keeps Delete disabled. Otherwise the user confirms by typing the type's
 * name, and the type's configurations are deleted with it. If a device was
 * given the type after the page loaded, the service's refusal shows as an
 * error in the dialog. The dialog stays open while the delete runs, so that
 * error always has somewhere to show.
 */
export function DeleteDeviceTypeDialog({
  deviceType,
  open,
  onClose,
  onDeleted,
}: DeleteDeviceTypeDialogProps) {
  const deleteDeviceType = useDeleteDeviceType();
  return (
    <Dialog
      role="alertdialog"
      open={open}
      onOpenChange={(next) => {
        if (!next && !deleteDeviceType.isPending) {
          onClose();
        }
      }}
    >
      <Dialog.Content>
        {open && (
          // Mounted per opening, so each opening starts from a clean form.
          <DeleteDeviceTypeForm
            deviceType={deviceType}
            deleteDeviceType={deleteDeviceType}
            onClose={onClose}
            onDeleted={onDeleted}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function DeleteDeviceTypeForm({
  deviceType,
  deleteDeviceType,
  onClose,
  onDeleted,
}: Omit<DeleteDeviceTypeDialogProps, "open"> & {
  deleteDeviceType: ReturnType<typeof useDeleteDeviceType>;
}) {
  const [confirmText, setConfirmText] = React.useState("");
  const hasDevices = deviceType.device_count > 0;
  const confirmed =
    !hasDevices && isDeleteConfirmed(confirmText, deviceType.name);

  const onConfirm = () => {
    if (!confirmed || deleteDeviceType.isPending) {
      return;
    }
    deleteDeviceType.mutate(deviceType, { onSuccess: onDeleted });
  };

  const devicesWithType =
    deviceType.device_count === 1
      ? "1 device has this type."
      : `${deviceType.device_count} devices have this type.`;

  return (
    <div className={styles.body}>
      <Dialog.Title>
        Delete device type &ldquo;{deviceType.name}&rdquo;?
      </Dialog.Title>
      {hasDevices ? (
        <Dialog.Description className={styles.description}>
          <span className={styles.status}>
            <TriangleAlert
              size={16}
              aria-hidden
              className={styles.warningIcon}
            />
            {devicesWithType}
          </span>
          <span>
            Before it can be deleted, change or clear the type on those devices:
            on the Devices page, select them, then choose Set device type…
          </span>
        </Dialog.Description>
      ) : (
        <>
          <Dialog.Description className={styles.description}>
            <span className={styles.status}>
              <CircleCheck size={16} aria-hidden className={styles.okIcon} />
              No devices have this type.
            </span>
            <span>Every configuration the type holds is deleted with it.</span>
          </Dialog.Description>
          {deleteDeviceType.error && (
            <Banner variant="error">
              {userFacingErrorMessage(deleteDeviceType.error)}
            </Banner>
          )}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Type <code>{deviceType.name}</code> to confirm
            </span>
            <TextInput
              autoComplete="off"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onConfirm();
                }
              }}
            />
          </label>
        </>
      )}
      <div className={styles.actions}>
        <RobotoButton
          variant="secondary"
          disabled={deleteDeviceType.isPending}
          onClick={onClose}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          variant="destructive"
          loading={deleteDeviceType.isPending}
          disabled={!confirmed || deleteDeviceType.isPending}
          onClick={onConfirm}
        >
          {hasDevices ? "Delete" : "Delete type and its configurations"}
        </RobotoButton>
      </div>
    </div>
  );
}
